import { useMemo, useState } from "react";
import { toast } from "react-toastify";

import Button from "components/v1/Button";
import DetailTab from "components/molecules/buyer-match/detail-tab";
import ListListingDetailBuyerMatch from "components/molecules/buyer-match/list-listing-detail-buyer-match";
import ModalDetailCustomer from "components/molecules/buyer-match/modal-detail-customer";
import ModalEditBuyerAgent from "components/molecules/buyer-match/modal-edit-buyer-agent";
import ModalKonfirmasi from "components/molecules/buyer-match/modal-konfirmasi";
import ModalLoader from "components/organism/ModalLoader";
import Tooltip from "components/atomic/shared/Tooltip";

// Assets
import IArchived from "assets/i-archived.js";
import IArrowShortail from "assets/i-arrow-shortail.js";
import IInfo from "assets/i-info.js";
import IEdit from "assets/i-edit.js";
import IMagnifier from "assets/i-magnifier.js";

// Ctx
import { useBuyerMatchCtx } from "contexts/buyer-match";

// Api
import {
  useGetDetailCustomer,
  useGetListListingCustomerInfinite,
} from "api/buyer-match/query";
import {
  useUpdateCustomer,
  useUpdateStatusCustomer,
} from "api/buyer-match/mutation";
import { useDebounce } from "hooks/useDebounce";

const DetailBuyer = () => {
  const {
    selectedLeadId,
    setSelectedLeadId,
    setIsOpenDetailBuyer,
    listPropertyCategories,
    countListingBuyerMatch,
    refetchListCustomer,
  } = useBuyerMatchCtx();

  const [isOpenModal, setIsOpenModal] = useState(""); // "edit" | "confirm" | "archive"
  const [submitData, setSubmitData] = useState(null);
  const [search, setSearch] = useState("");
  const [listCustomerParams, setListCustomerParams] = useState({
    q: "",
    status: "matched", // matched, sent, interested, not_interested
    page: 1,
    _limit: 100,
  });

  const { mutate: updateCustomerMutation } = useUpdateCustomer();
  const { mutate: updateStatusCustomerMutation } = useUpdateStatusCustomer();

  const onCloseModal = () => {
    setIsOpenModal("");
    setSubmitData(null);
  };

  const {
    data: detailCustomer,
    refetch: refetchDetailCustomer,
    isLoading: isLoadingDetailCustomer,
  } = useGetDetailCustomer({
    id: selectedLeadId,
    enabled: !!selectedLeadId,
  });

  const {
    data: listListingCustomer,
    isLoading: isLoadingListListing,
    refetch: refetchListListingCustomer,
    fetchNextPage,
    hasNextPage,
  } = useGetListListingCustomerInfinite({
    id: selectedLeadId,
    params: listCustomerParams,
    enabled: !!selectedLeadId,
  });

  const formatDetailCustomer = useMemo(() => {
    return detailCustomer?.data?.data ?? [];
  }, [detailCustomer]);

  const formatListListingCustomer = useMemo(() => {
    if (!listListingCustomer?.pages?.length) return [];

    return listListingCustomer.pages.flatMap((page) => {
      return page?.data?.data?.listings ?? [];
    });
  }, [listListingCustomer]);

  const formatValue = useMemo(() => {
    return {
      name: formatDetailCustomer?.name,
      whatsapp_number: formatDetailCustomer?.whatsapp_number?.replace(
        formatDetailCustomer?.dial_code ?? "+62",
        ""
      ),
      dial_code: formatDetailCustomer?.dial_code ?? "+62",
      listing_type_id: formatDetailCustomer?.listing_type?.id,
      property_type_id: formatDetailCustomer?.property_type?.id,
      min_surface_area: formatDetailCustomer?.min_surface_area,
      min_building_area: formatDetailCustomer?.min_building_area,
      min_price: parseInt(formatDetailCustomer?.min_price).toLocaleString(
        "ID-id"
      ),
      max_price: parseInt(formatDetailCustomer?.max_price).toLocaleString(
        "ID-id"
      ),
      bedroom: formatDetailCustomer?.bedroom,
      bathroom: formatDetailCustomer?.bathroom,
      is_archive: formatDetailCustomer?.is_archive,
      is_studio: formatDetailCustomer?.is_studio,
      unit_price_id: formatDetailCustomer?.unit_price_id,
      preferred_area_ids:
        formatDetailCustomer?.preferred_areas?.map((item) => item.id) ?? [],
    };
  }, [formatDetailCustomer]);

  const CategoryOptions = useMemo(() => {
    return listPropertyCategories?.data?.data?.map((item) => ({
      value: item.id,
      label: item.name,
    }));
  }, [listPropertyCategories]);

  const handleMoveTo = (status) => {
    setListCustomerParams({
      ...listCustomerParams,
      status,
    });
  };

  const { debounceFn } = useDebounce({
    onChange: (query) => {
      setListCustomerParams({
        ...listCustomerParams,
        q: query,
      });
    },
    delay: 500,
  });

  const onConfirmEditBuyerAgent = (data) => {
    setSubmitData(data);
    setIsOpenModal("confirm");
  };

  const handleToListBuyer = () => {
    setSelectedLeadId(null);
    setIsOpenDetailBuyer(false);
  };

  const onSubmitEditBuyerAgent = () => {
    const customer = submitData;

    if (!customer.min_building_area) {
      customer.min_building_area = 0;
    }

    if (!customer.min_surface_area) {
      customer.min_surface_area = 0;
    }

    if (!customer.bedroom) {
      customer.bedroom = 0;
    }

    if (!customer.bathroom) {
      customer.bathroom = 0;
    }

    customer.whatsapp_number = customer.dial_code + customer.whatsapp_number;

    const data = { customer };
    updateCustomerMutation(
      { customerId: formatDetailCustomer?.id, data },
      {
        onSuccess: () => {
          toast.success("Profil Customer berhasil diperbarui");
          refetchDetailCustomer();
          refetchListCustomer();
          handleToListBuyer();
        },
        onError: (error) => {
          toast.error(
            error?.response?.data?.message ?? "Gagal membuat profil customer"
          );
        },
        onSettled: () => {
          onCloseModal();
        },
      }
    );
  };

  const onSubmitArchiveBuyerAgent = () => {
    const data = {
      customer: {
        is_archive: true,
      },
    };
    updateStatusCustomerMutation(
      { customerId: formatDetailCustomer?.id, data },
      {
        onSuccess: () => {
          toast.success("Profil Customer berhasil diarsipkan");
          refetchDetailCustomer();
          refetchListCustomer();
          handleToListBuyer();
        },
        onError: (error) => {
          toast.error(
            error?.response?.data?.message ?? "Gagal membuat profil customer"
          );
        },
        onSettled: () => {
          onCloseModal();
        },
      }
    );
  };

  return (
    <>
      <div className="min-h-[calc(100vh-81px-73px-48px)] rounded-[4px] bg-white">
        <div className="px-4 py-2 flex items-center justify-between mb-2 border-b-[0.5px] border-solid border-[#D8D8D9]">
          <div className="flex items-center justify-between gap-[12px]">
            <Button
              icon={IArrowShortail}
              type="secondary"
              iconColor="#252526"
              onClick={handleToListBuyer}
            />
            <span className="text-[13px] leading-[21px] tracking-[-0.4px] font-inter-semibold text-[#252526]">
              {formatDetailCustomer?.name}
            </span>
            <div className="w-[0.5px] h-[22px] bg-[#D8D8D9]" />
            <span className="text-[13px] leading-[21px] tracking-[-0.4px] font-inter text-[#808285]">
              {formatDetailCustomer?.whatsapp_number}
            </span>
          </div>

          <div className="flex items-center gap-1">
            <Tooltip content="Detail Customer">
              <Button
                icon={IInfo}
                type="secondary"
                iconColor="#252526"
                className="!p-[10px]"
                onClick={() => setIsOpenModal("detail")}
              />
            </Tooltip>
            <Tooltip content="Ubah Profil Customer">
              <Button
                icon={IEdit}
                type="secondary"
                iconColor="#252526"
                className="!p-[10px]"
                onClick={() => setIsOpenModal("edit")}
              />
            </Tooltip>
            <Tooltip content="Arsipkan Customer">
              <Button
                icon={IArchived}
                type="negative"
                iconColor="#FD4949"
                className="!p-[10px]"
                onClick={() => setIsOpenModal("archive")}
              />
            </Tooltip>
          </div>
        </div>

        <div className="px-4 py-2 flex items-center justify-between">
          <DetailTab
            handleMoveTo={handleMoveTo}
            status={listCustomerParams.status}
            countListing={countListingBuyerMatch}
          />
          <div className="h-[32px] p-2 flex justify-center items-center gap-1 rounded-[4px] border-[1px] border-solid border-[#D8D8D9]">
            <IMagnifier />
            <input
              className="h-[24px] py-2 text-[11px] leading-[17px] tracking-[-0.4px] font-inter text-[#252526] placeholder:text-[#808285]"
              placeholder="Cari listing dengan judul"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                debounceFn(e.target.value);
              }}
            />
          </div>
        </div>

        <div className="px-4 py-2 overflow-x-auto">
          <ListListingDetailBuyerMatch
            detailCustomer={formatDetailCustomer}
            listListingCustomer={formatListListingCustomer}
            status={listCustomerParams.status}
            refetchListListingCustomer={refetchListListingCustomer}
            listCustomerParams={listCustomerParams}
            setListCustomerParams={setListCustomerParams}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            isSearch={!!search}
          />
        </div>
      </div>

      <ModalDetailCustomer
        isOpen={isOpenModal === "detail"}
        onClose={onCloseModal}
        detailCustomer={formatDetailCustomer}
      />

      <ModalEditBuyerAgent
        isOpen={isOpenModal === "edit"}
        onClose={onCloseModal}
        defaultValues={formatValue}
        options={CategoryOptions}
        onSubmit={onConfirmEditBuyerAgent}
      />

      <ModalKonfirmasi
        isOpen={isOpenModal === "confirm"}
        onClose={onCloseModal}
        title="Simpan Perubahan?"
        description="Profil customer akan diperbarui sesuai dengan perubahan yang Anda lakukan."
        leftBtnText="Kembali"
        rightBtnText="Simpan"
        leftBtnAction={onCloseModal}
        rightBtnAction={onSubmitEditBuyerAgent}
      />

      <ModalKonfirmasi
        isOpen={isOpenModal === "archive"}
        onClose={onCloseModal}
        title="Arsipkan Customer?"
        description="Profil customer ini akan dipindahkan ke Arsip. Anda masih dapat melihatnya di halaman Arsip."
        leftBtnText="Batal"
        rightBtnText="Arsipkan"
        leftBtnAction={onCloseModal}
        rightBtnAction={onSubmitArchiveBuyerAgent}
      />

      <ModalLoader open={isLoadingDetailCustomer || isLoadingListListing} />
    </>
  );
};

export default DetailBuyer;
